import { ADMIN_ROUTES, AdminErrorSchema, type AdminRouteName } from '@lastrouter/contracts';
import type { z } from 'zod';

/*
 * Cliente tipado da API do painel. Os tipos de entrada e saída saem do mesmo mapa (ADMIN_ROUTES)
 * que o serviço usa para validar; a resposta é conferida pelo esquema antes de chegar à tela.
 */

type Routes = typeof ADMIN_ROUTES;
type Route<N extends AdminRouteName> = Routes[N];

export type ApiResponse<N extends AdminRouteName> = Route<N> extends { response: infer S extends z.ZodType } ? z.output<S> : Blob;
type BodyOf<N extends AdminRouteName> = Route<N> extends { body: infer S extends z.ZodType } ? z.input<S> : never;
type ParamsOf<N extends AdminRouteName> = Route<N> extends { params: infer S extends z.ZodType } ? z.input<S> : never;
type QueryOf<N extends AdminRouteName> = Route<N> extends { query: infer S extends z.ZodType } ? Partial<z.input<S>> : never;

export type ApiArgs<N extends AdminRouteName> = ([BodyOf<N>] extends [never] ? { body?: never } : { body: BodyOf<N> }) &
  ([ParamsOf<N>] extends [never] ? { params?: never } : { params: ParamsOf<N> }) &
  ([QueryOf<N>] extends [never] ? { query?: never } : { query?: QueryOf<N> });

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly correlationId: string | null,
    readonly details: string[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const API_BASE = '/api/admin';

/** Token anti-CSRF da sessão: vai em toda requisição que muda estado. */
let csrfToken: string | null = null;
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function buildUrl(path: string, params?: Record<string, unknown>, query?: Record<string, unknown>): string {
  let p = path.replace(/:([a-z_]+)/g, (_m, name: string) => {
    const v = params?.[name];
    if (v === undefined || v === null) throw new Error(`parâmetro de rota ausente: ${name}`);
    return encodeURIComponent(String(v));
  });
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  if (s) p += `?${s}`;
  return `${API_BASE}${p}`;
}

async function errorFrom(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  const parsed = AdminErrorSchema.safeParse(body);
  if (parsed.success) {
    const e = parsed.data.error;
    return new ApiError(res.status, e.code, e.message, e.correlation_id, e.details ?? []);
  }
  return new ApiError(res.status, `http_${res.status}`, res.statusText || 'erro', res.headers.get('x-correlation-id'));
}

export async function api<N extends AdminRouteName>(name: N, args: ApiArgs<N> = {} as ApiArgs<N>): Promise<ApiResponse<N>> {
  const route = ADMIN_ROUTES[name] as { method: string; path: string; response?: z.ZodType };
  const a = args as { body?: unknown; params?: Record<string, unknown>; query?: Record<string, unknown> };
  const headers: Record<string, string> = { accept: 'application/json' };
  if (a.body !== undefined) headers['content-type'] = 'application/json';
  if (route.method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;

  let res: Response;
  try {
    res = await fetch(buildUrl(route.path, a.params, a.query), {
      method: route.method,
      headers,
      credentials: 'same-origin',
      ...(a.body !== undefined ? { body: JSON.stringify(a.body) } : {}),
    });
  } catch {
    throw new ApiError(0, 'network', 'sem conexão', null);
  }
  if (!res.ok) throw await errorFrom(res);
  if (!route.response) return (await res.blob()) as ApiResponse<N>;
  const json: unknown = await res.json();
  const parsed = route.response.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(res.status, 'invalid_response', 'resposta fora do contrato', res.headers.get('x-correlation-id'), parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`));
  }
  return parsed.data as ApiResponse<N>;
}

/** URL de download (CSV, zip): o navegador baixa direto, com o cookie da sessão. */
export function downloadUrl(name: 'auditExport' | 'exportCatalog' | 'conversationsExport', query?: Record<string, unknown>): string {
  return buildUrl(ADMIN_ROUTES[name].path, undefined, query);
}
